import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import "bpmn-js/dist/assets/diagram-js.css";
import Viewer from "bpmn-js/lib/Viewer";
import type { ModuleDeclaration } from "didi";
import React, { useEffect, useRef } from "react";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../ui-kit/typography/Text/Text";
import { getTemplateTagColor, IProcessTemplate } from "./create-workflow-templates";
import customModdleExtension from "./moddle/custom.json";
import "./create-workflow-template-details-modal.scss";
import customWFSharedElementsModule from "./moddle/customWFSharedElementsModule";

/** Wide enough that a typical five-to-seven step template reads without zooming out. */
const MODAL_WIDTH: number = 880;

/** Minimal slice of the bpmn-js canvas service used here (avoids `any`). */
interface IBpmnCanvas {
  zoom(mode: string, center: string): void;
  /** Re-reads the container's size after it changes. */
  resized(): void;
}

interface ITemplateDiagramProps {
  /** The template's own BPMN, straight from the backend catalog. */
  xml: string;
}

/**
 * Read-only BPMN preview, zoomed to fit — and re-fitted whenever its box changes, because
 * the box is whatever the dialog has left after the header, the details and the footer,
 * and that follows the viewport's height.
 *
 * `Viewer` (not `NavigatedViewer`/`Modeler`) is read-only by construction: no palette,
 * no context pad, no pan/zoom, nothing to edit.
 *
 * Lifecycle: KModal renders through rc-dialog with `destroyOnClose`, so this component
 * — and its container element — only exist while the modal is open. The viewer is
 * therefore created on mount (container ref already attached when the effect runs) and
 * destroyed by the effect cleanup when the modal closes or the tool unmounts. At most
 * one viewer is alive at a time, so an open/close cycle cannot accumulate them.
 */
const TemplateDiagram = ({ xml }: ITemplateDiagramProps): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect((): (() => void) | undefined => {
    const container: HTMLDivElement | null = containerRef.current;
    if (!container) return undefined;

    let cancelled: boolean = false;
    let canvas: IBpmnCanvas | undefined;
    // Same custom moddle + renderer pair the editor's own viewers register, so the
    // preview draws with the designer's shape styling instead of stock BPMN.
    const viewer: Viewer = new Viewer({
      container,
      width: "100%",
      height: "100%",
      moddleExtensions: { custom: customModdleExtension },
      additionalModules: [customWFSharedElementsModule() as unknown as ModuleDeclaration]
    });

    viewer
      .importXML(xml)
      .then((): void => {
        if (cancelled) return;
        const importedCanvas: IBpmnCanvas = viewer.get("canvas");
        importedCanvas.zoom("fit-viewport", "auto");
        canvas = importedCanvas;
      })
      .catch((): void => {
        // A diagram that fails to import leaves the empty canvas surface behind; there
        // is nothing to recover from here and nothing worth interrupting the user for.
      });

    // Only once the diagram is in: fitting an empty canvas would be a no-op anyway.
    const resizeObserver: ResizeObserver = new ResizeObserver((): void => {
      if (!canvas) return;
      canvas.resized();
      canvas.zoom("fit-viewport", "auto");
    });
    resizeObserver.observe(container);

    return (): void => {
      cancelled = true;
      resizeObserver.disconnect();
      viewer.destroy();
    };
  }, [xml]);

  return <div ref={containerRef} className="kuika-process-template-details__canvas" />;
};

export interface ICreateWorkflowTemplateDetailsModalProps {
  /** The template whose details are open; `undefined` keeps the modal closed. */
  template?: IProcessTemplate;
  onClose: () => void;
  onUseTemplate: (template: IProcessTemplate) => void;
}

/**
 * "Details" for one process template: the read-only diagram, then the template's name,
 * tag, meta line and description, with the decision in the dialog's own footer.
 *
 * The dialog has a fixed height (capped by the viewport) and the diagram is the part that
 * gives: the details and the footer keep their natural size and the diagram takes what is
 * left, so the whole thing fits without scrolling on a laptop screen as well as a tall one.
 */
const CreateWorkflowTemplateDetailsModal = ({
  template,
  onClose,
  onUseTemplate
}: ICreateWorkflowTemplateDetailsModalProps): React.ReactElement => {
  return (
    <KModal
      className="kuika-process-template-details"
      showModal={!!template}
      closeModal={onClose}
      header="Process template"
      closeTestId="processTemplateDetailsClose"
      // Nothing here loads after the modal opens, so the spinner wrapper would only add
      // a layer the flex chain below has to pass through.
      dontRenderSpinner
      width={MODAL_WIDTH}
      footer={
        template && (
          <KModalFooter
            onCancel={onClose}
            submitLabel="Use template"
            onSubmit={() => onUseTemplate(template)}
            submitTestId="processTemplateDetailsUseTemplate"
          >
            {/* Point-of-action disclosure, beside the button it qualifies: the caveat used
                to be a banner over the whole gallery, framing the feature by its limits
                before it showed any value. */}
            <span className="kuika-process-template-details__note">
              <KuikaSvg name="info-circle" width={14} height={14} />
              <KuikaSmallText>You wire up its data, parameters and actions after creating it.</KuikaSmallText>
            </span>
          </KModalFooter>
        )
      }
    >
      {template && (
        <div className="kuika-process-template-details__layout">
          <div className="kuika-process-template-details__diagram">
            <TemplateDiagram xml={template.xml} />
          </div>

          <div className="kuika-process-template-details__info">
            <div className="kuika-process-template-details__heading">
              <KuikaH5 className="kuika-process-template-details__name">{template.name}</KuikaH5>
              <KuikaBadge size="small" color={getTemplateTagColor(template.tag)} content={template.tag} />
              <span className="kuika-process-template-details__meta">
                <KuikaSmallText>Process Template</KuikaSmallText>
                <KuikaSvg name="layout-columns" width={12} height={12} />
                <KuikaSmallText>{`${template.stepCount} steps`}</KuikaSmallText>
              </span>
            </div>
            {template.description && (
              <KuikaText className="kuika-process-template-details__description">{template.description}</KuikaText>
            )}
          </div>
        </div>
      )}
    </KModal>
  );
};

export default CreateWorkflowTemplateDetailsModal;
